import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface PageNavCrumb {
  label: string;
  to?: string;
}

export interface PageNavHeaderProps {
  crumbs: PageNavCrumb[];
  title: string;
  back?: { to: string; label: string };
  actions?: ReactNode;
  className?: string;
}

const PageNavHeader = ({
  crumbs,
  title,
  back,
  actions,
  className,
}: PageNavHeaderProps) => (
  <header
    className={['mb-1 flex flex-col gap-2.5', className].filter(Boolean).join(' ')}
  >
    <div className="flex min-h-0 flex-wrap items-center justify-between gap-3">
      <nav
        className="flex min-w-0 flex-1 flex-wrap items-center text-[0.72rem] leading-[1.3] tracking-[0.02em]"
        aria-label="Breadcrumb"
      >
        {back && (
          <>
            <Link
              to={back.to}
              className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-white/[0.48] no-underline transition-colors duration-150 hover:text-[#8bd6f1]"
            >
              <span className="text-[0.85em] leading-none opacity-85" aria-hidden>
                ←
              </span>
              {back.label}
            </Link>
            {(crumbs.length > 0 || actions) && (
              <span className="mx-[7px] select-none text-white/[0.22]" aria-hidden>
                ·
              </span>
            )}
          </>
        )}
        {crumbs.map((crumb, index) => (
          <span key={`${crumb.label}-${index}`} className="inline-flex items-center">
            {index > 0 && (
              <span className="mx-[5px] text-white/[0.24]" aria-hidden>
                /
              </span>
            )}
            {crumb.to ? (
              <Link
                to={crumb.to}
                className="text-white/[0.48] no-underline transition-colors duration-150 hover:text-[#8bd6f1]"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="font-semibold text-primary/82">
                {crumb.label}
              </span>
            )}
          </span>
        ))}
      </nav>
      {actions && (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
    <h1 className="m-0 bg-[linear-gradient(90deg,#ffffff_0%,#c8e8f4_55%,#25b4e0_100%)] bg-clip-text font-display text-[clamp(1.2rem,2.5vw,1.45rem)] font-bold leading-[1.2] tracking-[0.01em] text-transparent [-webkit-background-clip:text]">
      {title}
    </h1>
  </header>
);

export default PageNavHeader;
